import React, { useContext, useEffect, useMemo, useState, useRef } from "react";
import {
  AppBar,
  Badge,
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  HandshakeOutlined,
  LogoutOutlined,
  WalletOutlined,
} from "@mui/icons-material";
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import { useNavigate } from "react-router-dom";
import Logo from "../../assets/image/logo-footer.png";
import ConfirmationModal from "../Shared/confirmationModal";
import { vendorMenuItems } from "../../config/vendorNavMenuConfig";
import { AccessGateContext } from "../../context/accessGateContext";
import { useAuth } from "../../services/providers/authProvider";
import { featureFlags } from "../../config/featureConfig";
import { VENDOR_PATHS } from "../../config/vendorNavPaths";
import { useNotificationUnread } from "../../context/notificationUnreadContext";
import { useTicketUnread } from "../../context/ticketUnreadContext";
import { httpClient } from "../../services/httpClient";
import api from "../../config/gatewayConfig";

const DRAWER_WIDTH = 240;
const MOBILE_APPBAR_HEIGHT_PX = 56;

const buildOpenSubmenusForRoute = (items, route) => {
  const open = {};
  items.forEach(({ text, subItems }) => {
    if (subItems?.some((sub) => sub.path === route)) {
      open[text] = true;
    }
  });
  return open;
};

function handleMenuButtonClick(item, handlers) {
  const { subItems, path } = item;
  if (path === VENDOR_PATHS.LOGOUT) {
    handlers.onLogout();
    return;
  }
  if (subItems) {
    handlers.handleSubmenuClick(item.text);
    return;
  }
  handlers.handleNavigate(path);
}

const VendorMenu = ({ activeRoute }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [modalLogoutOpen, setModalLogoutOpen] = useState(false);
  const [openSubmenus, setOpenSubmenus] = useState(() =>
    buildOpenSubmenusForRoute(vendorMenuItems.filter((item) => !item.hidden), activeRoute)
  );
  const { allowVendorAreaAccess } = useContext(AccessGateContext);
  const { logout, vendorId } = useAuth() || {};
  const { unreadCount } = useNotificationUnread();
  const { menuBadgeCount: ticketBadgeCount } = useTicketUnread();
  const [balance, setBalance] = useState(0.0);
  const [balanceIncreased, setBalanceIncreased] = useState(false);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (featureFlags.enableSaldoResiduo && vendorId) {
      if (isFirstLoad.current) {
         const cached = sessionStorage.getItem(`vendor_balance_${vendorId}`);
         if (cached) {
             setBalance(parseFloat(cached));
         }
         isFirstLoad.current = false;
      }

      const fetchBalance = async () => {
        try {
          const response = await httpClient.get(`${api.apiBaseUrl}/biling/customer/balance/${vendorId}`);
          const newBalance = response.data;
          
          const cached = sessionStorage.getItem(`vendor_balance_${vendorId}`);
          const oldBalance = cached ? parseFloat(cached) : 0.0;
          
          if (newBalance > oldBalance && cached !== null) {
            setBalanceIncreased(true);
            setTimeout(() => setBalanceIncreased(false), 3000);
          }
          
          setBalance(newBalance);
          sessionStorage.setItem(`vendor_balance_${vendorId}`, newBalance);
        } catch (error) {
          console.error("Errore durante il recupero del saldo residuo:", error);
        }
      };
      fetchBalance();
    }
  }, [vendorId]);

  const visibleMenuItems = useMemo(
    () =>
      vendorMenuItems
        .filter((item) => !item.hidden)
        .map((item) =>
          item.subItems ? { ...item, subItems: item.subItems.filter((sub) => !sub.hidden) } : item
        )
        .filter((item) => !item.subItems || item.subItems.length > 0),
    []
  );

  const closeMobile = () => {
    if (isMobile) setMobileOpen(false);
  };

  const handleClickLogout = () => {
    logout();
    navigate("/home");
  };

  const handleSubmenuClick = (text) => {
    setOpenSubmenus((prev) => ({
      ...prev,
      [text]: !prev[text],
    }));
  };

  const handleNavigate = (path) => {
    allowVendorAreaAccess();
    navigate(path);
    closeMobile();
  };

  useEffect(() => {
    visibleMenuItems.forEach(({ text, subItems }) => {
      if (subItems?.some((sub) => sub.path === activeRoute)) {
        setOpenSubmenus((prev) => (prev[text] ? prev : { ...prev, [text]: true }));
      }
    });
  }, [activeRoute, visibleMenuItems]);

  const drawerPaperSx = {
    width: DRAWER_WIDTH,
    boxSizing: "border-box",
    borderRadius: isMobile ? 0 : "0px 10px 10px 0px",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#0b3d87",
    color: "white",
    ...(isMobile
      ? {
          paddingTop: `calc(${MOBILE_APPBAR_HEIGHT_PX}px + env(safe-area-inset-top, 0px))`,
        }
      : {}),
  };

  const drawer = (
    <>
      {!isMobile ? (
        <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }} sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          <img src={Logo} alt="Wow Che Prezzi" style={{ width: "130px", height: "90px" }} />
        </Box>
      ) : null}

      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          paddingBottom: "100px",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <List sx={{ mx: 0.5 }}>
          {visibleMenuItems.map(({ text, path, icon, subItems, disabled }, index) => {
            const isMacroActive =
              (subItems && subItems.some((sub) => sub.path === activeRoute)) || path === activeRoute;
            const showNotifBadge =
              featureFlags.enableNotificationPolling &&
              path === VENDOR_PATHS.NOTIFICHE &&
              unreadCount > 0;
            const showTicketBadge =
              featureFlags.enableTicketPolling &&
              path === VENDOR_PATHS.TICKET &&
              ticketBadgeCount > 0;
            const badgeCount = showTicketBadge ? ticketBadgeCount : unreadCount;
            const menuIcon =
              (showNotifBadge || showTicketBadge) ? (
                <Badge
                  badgeContent={badgeCount > 99 ? "99+" : badgeCount}
                  color="error"
                  overlap="circular"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontFamily: "Rubik, sans-serif",
                      fontWeight: 700,
                      fontSize: 11,
                    },
                  }}
                >
                  {icon}
                </Badge>
              ) : (
                icon
              );
            return (
              <React.Fragment key={index}>
                <ListItem disablePadding>
                  <ListItemButton
                    disabled={disabled === true}
                    onClick={() =>
                      handleMenuButtonClick(
                        { text, subItems, path },
                        {
                          onLogout: () => setModalLogoutOpen(true),
                          handleSubmenuClick,
                          handleNavigate,
                        }
                      )
                    }
                    sx={{
                      fontFamily: "Rubik, sans-serif",
                      display: "flex",
                      justifyContent: "flex-start",
                      borderRadius: "10px",
                      backgroundColor: isMacroActive ? "white" : "inherit",
                      "&:hover": { backgroundColor: isMacroActive ? "#D0D0D0" : "#2a5cad" },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: isMacroActive ? "#0b3d87" : "white",
                        display: "flex",
                        justifyContent: "flex-start",
                      }}
                    >
                      {menuIcon}
                    </ListItemIcon>
                    <ListItemText
                      primary={<Typography sx={{ fontFamily: "Rubik, sans-serif" }}>{text}</Typography>}
                      sx={{
                        fontFamily: "Rubik, sans-serif",
                        color: isMacroActive ? "#0b3d87" : "white",
                        fontWeight: isMacroActive ? "bold" : "normal",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    />
                    {subItems ? (
                      openSubmenus[text] ? (
                        <ExpandLess sx={{ color: isMacroActive ? "#0b3d87" : "white" }} />
                      ) : (
                        <ExpandMore sx={{ color: isMacroActive ? "#0b3d87" : "white" }} />
                      )
                    ) : null}
                  </ListItemButton>
                </ListItem>
                {subItems ? (
                  <Collapse in={openSubmenus[text]} timeout="auto" unmountOnExit>
                    <Box sx={{ display: "flex" }}>
                      <Box
                        sx={{
                          width: 3,
                          backgroundColor: "white",
                          marginLeft: "10px",
                          my: "8px",
                          borderRadius: 2,
                        }}
                      />
                      <List component="div" disablePadding sx={{ pl: 0, width: "100%" }}>
                        {subItems.map(({ text: subText, path: subPath, icon: subIcon, onTap, disabled: subDisabled }, subIndex) => {
                          const isSubActive = activeRoute === subPath;
                          return (
                            <ListItem key={subIndex} disablePadding sx={{ width: "100%" }}>
                              <ListItemButton
                                disabled={subDisabled === true}
                                onClick={() => {
                                  allowVendorAreaAccess();
                                  if (onTap) {
                                    onTap();
                                  } else {
                                    navigate(subPath);
                                  }
                                  closeMobile();
                                }}
                                sx={{
                                  fontFamily: "Rubik, sans-serif",
                                  borderRadius: "10px",
                                  my: "3px",
                                  pl: 1,
                                  "&:hover": { backgroundColor: "#2a5cad" },
                                }}
                              >
                                <ListItemIcon sx={{ color: "white", display: "flex", justifyContent: "center" }}>
                                  {subIcon}
                                </ListItemIcon>
                                <Typography
                                  sx={{
                                    fontFamily: "Rubik, sans-serif",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    fontSize: 14,
                                    textOverflow: "ellipsis",
                                    color: "white",
                                    fontWeight: isSubActive ? "bold" : "normal",
                                  }}
                                >
                                  {subText}
                                </Typography>
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Box>
                  </Collapse>
                ) : null}
              </React.Fragment>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          py: 2,
          bgcolor: "#e00036",
          color: "white",
          textAlign: "center",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          borderTopLeftRadius: 10,
          borderTopRightRadius: 10,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {featureFlags.enableSaldoResiduo ? <WalletOutlined /> : <HandshakeOutlined />}
          <Typography variant="body1" sx={{ fontFamily: "Rubik, sans-serif" }}>
            {featureFlags.enableSaldoResiduo ? "Il tuo saldo residuo" : "Per te Wow Che Prezzi è ..."}
          </Typography>
        </Box>
        {featureFlags.enableSaldoResiduo ? (
          <Typography 
            variant="h4" 
            sx={{ 
              fontFamily: "Rubik, sans-serif", 
              fontWeight: "bold",
              color: balanceIncreased ? "#4caf50" : "white",
              transition: "all 0.5s ease-in-out",
              transform: balanceIncreased ? "scale(1.1)" : "scale(1)",
            }}
          >
            {typeof balance === "number" ? balance.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : balance}€
          </Typography>
        ) : (
          <>
            <Typography variant="h4" sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold" }} component="div">
              GRATIS
            </Typography>
            <Typography fontSize={14} sx={{ fontFamily: "Rubik, sans-serif", fontWeight: "bold" }}>
              fino al 31/12/26
            </Typography>
          </>
        )}
      </Box>
    </>
  );

  return (
    <>
      {isMobile ? (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            zIndex: (t) => t.zIndex.drawer + 1,
            backgroundColor: "#0b3d87",
            color: "#ffffff",
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            borderBottom: "1px solid rgba(255,255,255,0.12)",
            pt: "env(safe-area-inset-top, 0px)",
          }}
        >
          <Toolbar variant="dense" sx={{ minHeight: MOBILE_APPBAR_HEIGHT_PX, px: 1, position: "relative" }}>
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setMobileOpen((open) => !open)}
              aria-label={mobileOpen ? "Chiudi menu" : "Apri menu"}
              sx={{ color: "#ffffff", width: 48, height: 48 }}
            >
              <MenuRoundedIcon />
            </IconButton>
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Box
                component="img"
                src={Logo}
                alt="Wow Che Prezzi"
                sx={{ height: 36, width: "auto", maxWidth: "min(52vw, 200px)", objectFit: "contain" }}
              />
            </Box>
            <Box sx={{ width: 48, height: 48, flexShrink: 0 }} aria-hidden />
          </Toolbar>
        </AppBar>
      ) : null}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? mobileOpen : true}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        anchor="left"
        sx={{
          width: isMobile ? undefined : DRAWER_WIDTH,
          flexShrink: 0,
          "& .MuiDrawer-paper": drawerPaperSx,
        }}
      >
        {drawer}
      </Drawer>

      <ConfirmationModal
        open={modalLogoutOpen}
        onClose={() => setModalLogoutOpen(false)}
        title="Sei sicuro di voler effettuare il logout?"
        subtitle=""
        message="Dovrai inserire nuovamente le tue credenziali per accedere all'area venditore."
        icon={<LogoutOutlined sx={{ color: "white", fontSize: 50 }} />}
        onConfirm={() => {
          handleClickLogout();
          setModalLogoutOpen(false);
        }}
      />
    </>
  );
};

export default VendorMenu;
